import { useLogger } from "@bindery/logging/react";
import { RaisedDialogContent } from "@bindery/ui/components/reader/raised-dialog-content";
import { Button } from "@bindery/ui/components/ui/button";
import {
  Dialog,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@bindery/ui/components/ui/dialog";
import { cn } from "@bindery/ui/lib/utils";
import {
  ArrowUpOnSquareIcon,
  ClipboardDocumentIcon,
} from "@heroicons/react/20/solid";
import {
  type KeyboardEvent,
  type PointerEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { SITE_NAME } from "@/site";
import { useNickname } from "@/storage/nickname";
import type { BookmarkLinkRefusal } from "./bookmark-link";
import {
  type BookmarkPlatform,
  browserBookmarkPlatform,
} from "./bookmark-platform";
import {
  type AddressBar,
  browserAddressBar,
  useAddressSwap,
} from "./use-address-swap";
import {
  type BookmarkLinkDeps,
  type BookmarkLinkState,
  browserBookmarkLinkDeps,
  useBookmarkLink,
} from "./use-bookmark-link";

// Above the fixed navbar capsule (z-60), which a centred dialog of this
// height otherwise slides under; the overlay rises with it so the capsule
// is dimmed like the rest of the page.
const ABOVE_NAVBAR = "z-[80]";

export interface BookmarkDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  readerId: string;
  onCloseAutoFocus?: (event: Event) => void;
  deps?: BookmarkLinkDeps;
  platform?: BookmarkPlatform;
  addressBar?: AddressBar;
}

export function BookmarkDialog({
  open,
  onOpenChange,
  readerId,
  onCloseAutoFocus,
  deps = browserBookmarkLinkDeps,
  platform,
  addressBar = browserAddressBar,
}: BookmarkDialogProps) {
  const nickname = useNickname(readerId);
  const close = useCallback(() => onOpenChange(false), [onOpenChange]);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <RaisedDialogContent
        className={cn(ABOVE_NAVBAR, "sm:max-w-md")}
        overlayClassName={ABOVE_NAVBAR}
        onCloseAutoFocus={onCloseAutoFocus}
      >
        <DialogHeader>
          <DialogTitle className="text-xl tracking-tight">
            Bookmark this reader
          </DialogTitle>
          <DialogDescription>
            A link that brings {nickname ?? "your reader"} straight back here.
            No account, no sign-in.
          </DialogDescription>
        </DialogHeader>
        <BookmarkDialogBody
          name={nickname}
          close={close}
          deps={deps}
          platform={platform ?? browserBookmarkPlatform()}
          addressBar={addressBar}
        />
      </RaisedDialogContent>
    </Dialog>
  );
}

type Notice =
  | "copied"
  | "copy_failed"
  | "share_failed"
  | "link_reset"
  | "reset_rate_limited"
  | "reset_unavailable"
  | null;

function copyFailedText(platform: BookmarkPlatform): string {
  if (platform.desktop)
    return `Couldn’t copy. Press ${platform.shortcut} to bookmark instead.`;
  if (platform.share !== null) return "Couldn’t copy. Try Share link instead.";
  return "Couldn’t copy the link.";
}

function noticeText(notice: NonNullable<Notice>, platform: BookmarkPlatform) {
  switch (notice) {
    case "copied":
      return "Link copied.";
    case "copy_failed":
      return copyFailedText(platform);
    case "share_failed":
      return "Couldn’t share. Try Copy link instead.";
    case "link_reset":
      return "New link ready. The old one won’t open for anyone new.";
    case "reset_rate_limited":
      return "That’s a lot of new links for one hour. The old link still works; try again later.";
    case "reset_unavailable":
      return "Couldn’t reset the link right now. The old link still works.";
  }
}

const RESET_NOTICE: Record<BookmarkLinkRefusal, Notice> = {
  rate_limited: "reset_rate_limited",
  unavailable: "reset_unavailable",
  no_reader: "reset_unavailable",
};

const isCancelled = (error: unknown) =>
  error instanceof DOMException && error.name === "AbortError";

function BookmarkDialogBody({
  name,
  close,
  deps,
  platform,
  addressBar,
}: {
  name: string | null;
  close: () => void;
  deps: BookmarkLinkDeps;
  platform: BookmarkPlatform;
  addressBar: AddressBar;
}) {
  const { state, reset, retry } = useBookmarkLink(deps);
  const log = useLogger();
  const [notice, setNotice] = useState<Notice>(null);
  const [confirmingReset, setConfirmingReset] = useState(false);
  const url = state.status === "ready" ? state.url : null;
  const title = bookmarkTitle(name);
  const copyRef = useRef<HTMLButtonElement>(null);
  const focusCopyWhenIdle = useRef(false);

  useAddressSwap(url === null ? null : { href: url, title }, close, addressBar);

  const refusal = state.status === "refused" ? state.reason : null;
  useEffect(() => {
    if (refusal !== null)
      log.event("reading.bookmark_link.refused", { reason: refusal });
  }, [refusal, log]);

  // The reset confirmation unmounts with its focused button, so focus would
  // fall to the dialog; Copy link is the next thing to do with a new link.
  const resetting = state.status === "ready" && state.resetting;
  useEffect(() => {
    if (resetting || !focusCopyWhenIdle.current) return;
    focusCopyWhenIdle.current = false;
    copyRef.current?.focus();
  }, [resetting]);

  const confirmReset = () => {
    setConfirmingReset(false);
    setNotice(null);
    focusCopyWhenIdle.current = true;
    void reset().then((outcome) => {
      if (outcome === null) return;
      setNotice(outcome === "reset" ? "link_reset" : RESET_NOTICE[outcome]);
      log.event("reading.bookmark_link.reset", { outcome });
    });
  };

  const copy = useCallback(() => {
    if (url === null) return;
    platform.copy(url).then(
      () => {
        setNotice("copied");
        log.event("reading.bookmark_link.saved", { method: "copy" });
      },
      () => setNotice("copy_failed"),
    );
  }, [url, platform, log]);

  const share = useCallback(() => {
    if (url === null || platform.share === null) return;
    platform.share({ url, title }).then(
      () => log.event("reading.bookmark_link.saved", { method: "share" }),
      (error: unknown) => {
        if (!isCancelled(error)) setNotice("share_failed");
      },
    );
  }, [url, platform, title, log]);

  if (state.status !== "ready") {
    return <LinkPending state={state} retry={retry} />;
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-start gap-5">
        <BookmarkRibbon
          url={state.url}
          title={title}
          name={name}
          draggable={platform.desktop}
          onCopy={copy}
          onDrag={() =>
            log.event("reading.bookmark_link.saved", { method: "drag" })
          }
        />
        <SaveInstructions platform={platform} />
      </div>

      <div className="flex flex-wrap gap-2">
        <Button
          ref={copyRef}
          type="button"
          onClick={copy}
          disabled={state.resetting}
        >
          <ClipboardDocumentIcon className="h-4 w-4" />
          Copy link
        </Button>
        {platform.share !== null && (
          <Button
            type="button"
            variant="outline"
            onClick={share}
            disabled={state.resetting}
          >
            <ArrowUpOnSquareIcon className="h-4 w-4" />
            Share link
          </Button>
        )}
      </div>
      <output
        aria-live="polite"
        className={cn(
          "block min-h-5 text-sm",
          notice === "copied" || notice === "link_reset"
            ? "text-ear-teal"
            : "text-foreground",
        )}
      >
        {notice === null ? "" : noticeText(notice, platform)}
      </output>

      <p className="text-muted-foreground text-sm">
        Anyone with this link can read as {name ?? "this reader"}. It isn’t a
        password, so keep it between you.
      </p>

      <div className="border-t pt-4">
        {confirmingReset ? (
          <ResetConfirmation
            resetting={state.resetting}
            onConfirm={confirmReset}
            onKeep={() => setConfirmingReset(false)}
          />
        ) : (
          <div className="flex items-center justify-between gap-3 text-sm">
            <span className="text-muted-foreground">Want a fresh link?</span>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              disabled={state.resetting}
              onClick={() => setConfirmingReset(true)}
            >
              {state.resetting ? "Resetting…" : "Reset link"}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}

function SaveInstructions({ platform }: { platform: BookmarkPlatform }) {
  if (platform.desktop) {
    return (
      <div className="text-foreground flex flex-col gap-3 pt-1 text-sm">
        <p>
          Press <Shortcut keys={platform.shortcut} /> to bookmark this page.
          While this is open, your address bar holds the link.
        </p>
        <p className="text-muted-foreground">
          Or drag the bookmark onto your bookmarks bar.
        </p>
      </div>
    );
  }
  return (
    <p className="text-foreground pt-1 text-sm">
      {platform.share !== null
        ? "Share the link to yourself, or copy it, and keep it somewhere handy."
        : "Copy the link and keep it somewhere handy."}
    </p>
  );
}

export function bookmarkTitle(name: string | null): string {
  return name === null ? SITE_NAME : `${SITE_NAME} – ${name}`;
}

function Shortcut({ keys }: { keys: string }) {
  return (
    <kbd className="bg-muted text-foreground rounded-md border border-b-2 px-1.5 py-0.5 font-sans text-xs font-semibold">
      {keys}
    </kbd>
  );
}

const PRIMARY_BUTTON = 0;

/**
 * The ribbon is the link itself: a browser drags an anchor onto its
 * bookmarks bar with the anchor's title as the bookmark's name. The href is
 * there only while the primary button is down, which is when a drag can
 * start, so a middle click, a right-click menu or a modifier click never
 * finds a link to open the token page with; a plain click or Enter copies.
 */
function BookmarkRibbon({
  url,
  title,
  name,
  draggable,
  onCopy,
  onDrag,
}: {
  url: string;
  title: string;
  name: string | null;
  draggable: boolean;
  onCopy: () => void;
  onDrag: () => void;
}) {
  const [armed, setArmed] = useState(false);
  const disarm = () => setArmed(false);
  const arm = (event: PointerEvent<HTMLAnchorElement>) => {
    if (draggable && event.button === PRIMARY_BUTTON) setArmed(true);
  };
  const copyOnKey = (event: KeyboardEvent<HTMLAnchorElement>) => {
    if (event.key !== "Enter" && event.key !== " ") return;
    event.preventDefault();
    onCopy();
  };
  return (
    <a
      // oxlint-disable-next-line jsx-a11y/prefer-tag-over-role -- only an anchor drags onto a bookmarks bar as a named link; a button would drag as text
      role="button"
      tabIndex={0}
      href={armed ? url : undefined}
      title={title}
      draggable={draggable}
      onPointerDown={arm}
      onPointerUp={disarm}
      onPointerCancel={disarm}
      onDragEnd={disarm}
      onBlur={disarm}
      onDragStart={onDrag}
      onAuxClick={(event) => event.preventDefault()}
      onKeyDown={copyOnKey}
      onClick={(event) => {
        event.preventDefault();
        onCopy();
      }}
      aria-label={
        draggable
          ? `${title}: drag to your bookmarks bar, or press to copy the link`
          : `${title}: press to copy the link`
      }
      className={cn(
        "block shrink-0 rounded-sm p-1 select-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none",
        draggable ? "cursor-grab active:cursor-grabbing" : "cursor-pointer",
      )}
    >
      <span
        aria-hidden="true"
        className="bg-ear-teal flex h-36 w-24 flex-col justify-between px-3 pt-3 pb-8 text-white [clip-path:polygon(0_0,100%_0,100%_100%,50%_84%,0_100%)]"
      >
        <span className="text-[11px] leading-tight text-white/80">
          Read by Ear
        </span>
        <span className="line-clamp-2 text-base leading-tight font-semibold break-words">
          {name ?? "My reader"}
        </span>
      </span>
    </a>
  );
}

function ResetConfirmation({
  resetting,
  onConfirm,
  onKeep,
}: {
  resetting: boolean;
  onConfirm: () => void;
  onKeep: () => void;
}) {
  const keepRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    keepRef.current?.focus();
  }, []);
  return (
    <div className="flex flex-col gap-3 text-sm">
      <p className="text-foreground font-semibold">Reset the link?</p>
      <p className="text-muted-foreground">
        The old link stops working for anyone who hasn’t opened it yet. Someone
        who already has can keep reading.
      </p>
      <div className="flex flex-wrap gap-2">
        <Button
          ref={keepRef}
          type="button"
          variant="outline"
          size="sm"
          onClick={onKeep}
          disabled={resetting}
        >
          Keep it
        </Button>
        <Button
          type="button"
          variant="destructive"
          size="sm"
          onClick={onConfirm}
          disabled={resetting}
        >
          Reset the link
        </Button>
      </div>
    </div>
  );
}

const REFUSAL_TEXT: Record<BookmarkLinkRefusal, string> = {
  no_reader:
    "This sign-in can’t make a bookmark link. If you opened Read by Ear from a saved link or card, that link is the bookmark.",
  rate_limited:
    "That’s a lot of new links for one hour. Try again a little later.",
  unavailable:
    "Couldn’t get the link right now. Check your connection and try again.",
};

function LinkPending({
  state,
  retry,
}: {
  state: Exclude<BookmarkLinkState, { status: "ready" }>;
  retry: () => void;
}) {
  if (state.status === "loading") {
    return (
      <div className="flex items-start gap-5" aria-busy="true">
        <div className="bg-muted h-36 w-24 shrink-0 animate-pulse rounded-sm [clip-path:polygon(0_0,100%_0,100%_100%,50%_84%,0_100%)]" />
        <output className="text-muted-foreground block pt-1 text-sm">
          Getting the link…
        </output>
      </div>
    );
  }
  return (
    <div className="flex flex-col gap-4">
      <p role="alert" className="text-foreground text-sm">
        {REFUSAL_TEXT[state.reason]}
      </p>
      {state.reason !== "no_reader" && (
        <div>
          <Button type="button" variant="outline" onClick={retry}>
            Try again
          </Button>
        </div>
      )}
    </div>
  );
}
